import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import './HelperBot.css';

const CHAT_STORAGE_PREFIX = 'tenlitor_helper_bot_chat';

const WELCOME_MESSAGE = {
  role: 'bot',
  text: 'היי! 👋 אני בוט הAI של תן לי תור.\nאפשר לשאול אותי כל שאלה על המערכת, ואני אעזור לך למצוא את מה שאתה צריך.',
};

const SUGGESTED_QUESTIONS = [
  'איך אני קובע שעות פעילות?',
  'איך משתפים קישור הזמנה?',
  'איך מוסיפים שירות חדש?',
  'איך עובד בוט הוואטסאפ?',
];

function parseActions(text) {
  const actions = [];
  const cleaned = text.replace(/```action\s*\n?([\s\S]*?)\n?```/g, (_match, json) => {
    try {
      const parsed = JSON.parse(json.trim());
      if (parsed && parsed.type && parsed.path) {
        actions.push(parsed);
      }
    } catch { /* ignore bad JSON */ }
    return '';
  });
  return { cleaned: cleaned.trim(), actions };
}

function getChatStorageKey(user) {
  const stableId = user?.id || user?.business_id || user?.owner_phone || user?.phone || 'default';
  return `${CHAT_STORAGE_PREFIX}:${stableId}`;
}

function renderFormattedLine(line) {
  const parts = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIdx = 0;
  let match;
  let keyIdx = 0;

  while ((match = regex.exec(line)) !== null) {
    const full = match[0];
    const content = match[1];
    const start = match.index;
    const end = start + full.length;

    if (start > lastIdx) {
      parts.push(<span key={`txt-${keyIdx++}`}>{line.slice(lastIdx, start)}</span>);
    }
    parts.push(<strong key={`b-${keyIdx++}`}>{content}</strong>);
    lastIdx = end;
  }

  if (lastIdx < line.length) {
    parts.push(<span key={`txt-${keyIdx++}`}>{line.slice(lastIdx)}</span>);
  }

  if (parts.length === 0) return line;
  return parts;
}

export default function HelperBot({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const key = getChatStorageKey(user);
      const raw = localStorage.getItem(key);
      if (!raw) return [WELCOME_MESSAGE];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed?.messages) || parsed.messages.length === 0) return [WELCOME_MESSAGE];
      return parsed.messages;
    } catch {
      return [WELCOME_MESSAGE];
    }
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [usageInfo, setUsageInfo] = useState(() => {
    try {
      const key = getChatStorageKey(user);
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.usageInfo || null;
    } catch {
      return null;
    }
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const storageStableId = user?.id || user?.business_id || user?.owner_phone || user?.phone || 'default';
  const chatStorageKey = useMemo(
    () => `${CHAT_STORAGE_PREFIX}:${storageStableId}`,
    [storageStableId]
  );

  useEffect(() => {
    try {
      localStorage.setItem(chatStorageKey, JSON.stringify({ messages, usageInfo }));
    } catch {
      // Ignore localStorage quota or access errors
    }
  }, [chatStorageKey, messages, usageInfo]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(chatStorageKey);
      if (!raw) {
        setMessages([WELCOME_MESSAGE]);
        setUsageInfo(null);
        return;
      }
      const parsed = JSON.parse(raw);
      const storedMessages = Array.isArray(parsed?.messages) && parsed.messages.length > 0
        ? parsed.messages
        : [WELCOME_MESSAGE];
      setMessages(storedMessages);
      setUsageInfo(parsed?.usageInfo || null);
    } catch {
      setMessages([WELCOME_MESSAGE]);
      setUsageInfo(null);
    }
  }, [chatStorageKey]);

  const scrollToBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (container) container.scrollTop = container.scrollHeight;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => scrollToBottom());
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, scrollToBottom]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || loading) return;

    const userMsg = { role: 'user', text: trimmed };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const history = messages
        .filter(m => m.role === 'user' || m.role === 'bot')
        .slice(-10)
        .map(m => ({ role: m.role === 'bot' ? 'assistant' : 'user', text: m.text }));

      const res = await api.post('/business/helper-bot', {
        message: trimmed,
        history,
      });

      const { reply, used, limit } = res.data;
      setUsageInfo({ used, limit });

      const { cleaned, actions } = parseActions(reply);

      const botMsg = {
        role: 'bot',
        text: cleaned || reply,
        actions: actions.length > 0 ? actions : undefined,
      };
      setMessages(prev => [...prev, botMsg]);
    } catch (err) {
      const errData = err?.response?.data;
      if (errData?.error === 'monthly_limit_reached') {
        setMessages(prev => [...prev, {
          role: 'bot',
          text: errData.message || 'הגעת למגבלת ההודעות החודשית.',
        }]);
        setUsageInfo({ used: errData.used, limit: errData.limit });
      } else {
        setMessages(prev => [...prev, {
          role: 'bot',
          text: 'סליחה, משהו השתבש. נסה שוב בעוד כמה רגעים.',
        }]);
      }
    } finally {
      setLoading(false);
    }
  }, [loading, messages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleAction = (action) => {
    if (action.type === 'navigate' && action.path) {
      navigate(action.path);
      setOpen(false);
    }
  };

  const handleSuggestion = (q) => {
    sendMessage(q);
  };

  const pathname = String(location?.pathname || '').toLowerCase();
  const topCenterPaths = [
    '/appointments',
    '/schedule',
    '/waitlist',
    '/services',
    '/customers',
    '/staff',
    '/analytics',
    '/business-status',
    '/rating',
  ];
  const useTopCenterPosition = topCenterPaths.some((path) => pathname === path || pathname.startsWith(`${path}/`));

  if (!user) return null;

  return (
    <>
      {/* Floating toggle button */}
      <button
        type="button"
        className={`helper-bot-fab ${open ? 'helper-bot-fab--open' : ''} ${useTopCenterPosition ? 'helper-bot-fab--top-center' : ''}`}
        onClick={() => setOpen(prev => !prev)}
        aria-label={open ? 'סגור בוט הAI של תן לי תור' : 'פתח בוט הAI של תן לי תור'}
        title="בוט הAI של תן לי תור"
      >
        {open ? '✕' : '💬'}
      </button>

      {/* Chat panel */}
      {open && (
        <div className={`helper-bot-panel ${useTopCenterPosition ? 'helper-bot-panel--top-center' : ''}`} dir="rtl">
          <div className="helper-bot-header">
            <div className="helper-bot-header-main">
              <span className="helper-bot-header-title">🤖 בוט הAI של תן לי תור</span>
              <button
                type="button"
                className="helper-bot-help-btn"
                onClick={() => navigate('/help')}
                aria-label="פתח עזרה ו-FAQ"
                title="עזרה ו-FAQ"
              >
                עזרה / FAQ
              </button>
            </div>
            {usageInfo && (
              <span className="helper-bot-usage">
                {usageInfo.limit - usageInfo.used} הודעות נותרו
              </span>
            )}
            <button
              type="button"
              className="helper-bot-close"
              onClick={() => setOpen(false)}
              aria-label="סגור"
            >
              ✕
            </button>
          </div>

          <div ref={messagesContainerRef} className="helper-bot-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`helper-bot-msg helper-bot-msg--${msg.role}`}>
                <div className="helper-bot-msg-bubble">
                  {msg.text.split('\n').map((line, j) => (
                    <span key={j}>
                      {renderFormattedLine(line)}
                      {j < msg.text.split('\n').length - 1 && <br />}
                    </span>
                  ))}
                </div>
                {msg.actions && msg.actions.length > 0 && (
                  <div className="helper-bot-actions">
                    {msg.actions.map((action, k) => (
                      <button
                        key={k}
                        type="button"
                        className="helper-bot-action-btn"
                        onClick={() => handleAction(action)}
                      >
                        {action.label || action.path}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="helper-bot-msg helper-bot-msg--bot">
                <div className="helper-bot-msg-bubble helper-bot-typing">
                  <span /><span /><span />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Suggested questions (only show when just the welcome message) */}
          {messages.length === 1 && (
            <div className="helper-bot-suggestions">
              {SUGGESTED_QUESTIONS.map((q, i) => (
                <button
                  key={i}
                  type="button"
                  className="helper-bot-suggestion"
                  onClick={() => handleSuggestion(q)}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          <form className="helper-bot-input-row" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              className="helper-bot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="שאל אותי משהו..."
              disabled={loading}
              maxLength={500}
              autoComplete="off"
            />
            <button
              type="submit"
              className="helper-bot-send"
              disabled={loading || !input.trim()}
              aria-label="שלח"
            >
              ➤
            </button>
          </form>
        </div>
      )}
    </>
  );
}
